import React, { memo, useMemo, useContext, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useRequest, useMemoizedFn } from 'ahooks';
import { H3 } from 'tea-component';
import { context as Context } from '@gp/components/Context';
import NoData from '@gp/components/NoData';
import TriggerViewCard from '@gp/components/TriggerViewCard';
import GeneralDownloadBtn from '@gp/components/GeneralDownloadBtn';
import BarLineEchartCon from '../RevenueProfitCompletionBarLineEChartCon';
// import PDFDownloadTable from '../PDFDownloadTable';
import MergeableTable from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Finance/Details/components/RevenueCompletion/components/MergeableTable';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import {
  queryProfitCompletionBarLineDataOfFinance,
  downloadProfitCompletionDataOfFinance,
  queryAllProfitCompletionBarLineDataOfFinance,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverallDetail';
import { getFirstDayOfYear, getLastDayOfYear } from '@gp/utils/common';
import { getStudioModuleName } from '../../../../../utils';
import {
  getBudgetActualForecastCompleteData,
  getBudgetActualForecastCompleteTableData,
  getProfitCompletionPDFDownloadTableMetadata,
  isBudgetActualOrForecastCompleteNoData,
  isRevenueCompletionTableNoData,
} from '../../utils';
import { FILE_TYPES1, CONSTANT_KEYS, FILE_TYPES_MAP } from '@gp/constants/const';
import './index.scss';

// Profit Completion模块名称
const MODULE_NAME = 'Net Profit Completion';

// Profit Completion模块 PDF下载 需要用到的className
const PDF_DOWNLOAD_NODE_CLASSNAME =
  'dbg-studio-overall-details-finance-profit-completion-pdf-card';

const budgetActualOrForecastCompleteDatacomparator = (
  a: IBudgetActualOrForecastCompleteData,
  b: IBudgetActualOrForecastCompleteData,
): number => {
  const aDateM = moment(a?.date);
  const bDateM = moment(b?.date);
  if (aDateM.isSame(bDateM)) return 0;
  return aDateM.isAfter(bDateM) ? 1 : -1;
};

const sortBudgetActualOrForecastCompleteData = (
  data: IBudgetActualOrForecastCompleteData[],
): IBudgetActualOrForecastCompleteData[] => {
  if (!Array.isArray(data)) return [];
  // date是有效日期的数据集合
  let temp1: IBudgetActualOrForecastCompleteData[] = [];
  // date是YTM的数据集合
  const temp2: IBudgetActualOrForecastCompleteData[] = [];
  // date是未知数据的数据集合
  const temp3: IBudgetActualOrForecastCompleteData[] = [];
  // date是空的数据集合
  const temp4: IBudgetActualOrForecastCompleteData[] = [];

  for (const item of data) {
    if (!item) continue;
    const date = item?.date;

    if (date) {
      const dateM = moment(date);
      if (dateM.isValid()) {
        temp1.push(item);
      } else if (date?.toLowerCase() === CONSTANT_KEYS.YTM) {
        temp2.push(item);
      } else {
        temp3.push(item);
      }
    } else {
      temp4.push(item);
    }
  }

  temp1 = temp1.sort(budgetActualOrForecastCompleteDatacomparator);

  return [...temp1, ...temp2, ...temp3, ...temp4];
};

interface IProfitCompletionProps extends IBaseProps {
  filter: IStudioOverallDetailsFilter;
}

const ProfitCompletion: React.FC<IProfitCompletionProps> = (props) => {
  const { filter, style, className } = props;

  const { studioId, studioName } =
    useContext<StudioOverallDetailContextTypes>(Context) || {};

  const {
    data: profitCompletionData,
    loading: profitCompletionLoading,
    run: profitCompletionRun,
  } = useRequest(queryProfitCompletionBarLineDataOfFinance, {
    manual: true,
  });

  const profitCompletionTrendsData = useMemo<
    IBudgetActualOrForecastCompleteData[]
  >(() => {
    if (!Array.isArray(profitCompletionData?.trend)) return [];
    return sortBudgetActualOrForecastCompleteData(
      getBudgetActualForecastCompleteData(profitCompletionData?.trend),
    );
  }, [profitCompletionData?.trend]);

  const profitCompletionTrendsDataWithoutYTM = useMemo<
    IBudgetActualOrForecastCompleteData[]
  >(() => {
    if (!Array.isArray(profitCompletionTrendsData)) return [];
    // 柱线图过滤掉YTM的数据
    return profitCompletionTrendsData?.filter(
      (item) => item && moment(item?.date).isValid(),
    );
  }, [profitCompletionTrendsData]);

  const profitCompletionTablesData = useMemo(() => {
    const tablesData = profitCompletionData?.table;
    if (!Array.isArray(tablesData)) return [];
    return getBudgetActualForecastCompleteTableData(tablesData);
  }, [profitCompletionTrendsData]);

  const profitCompletionTablesTotal = useMemo<number>(() => {
    // @ts-ignore
    return Number.parseInt(profitCompletionData?.total, 10);
  }, [profitCompletionData?.total]);

  const {
    data: allProfitCompletionData,
    loading: allProfitCompletionLoading,
    runAsync: allProfitCompletionRun,
  } = useRequest(queryAllProfitCompletionBarLineDataOfFinance, {
    manual: true,
  });

  const allRevenueCompletionTablesData = useMemo<
    IBudgetActualOrForecastCompleteTableData[]
  >(() => {
    const tablesData = allProfitCompletionData?.table;
    if (!Array.isArray(tablesData)) return [];
    return getBudgetActualForecastCompleteTableData(tablesData);
  }, [allProfitCompletionData?.table]);

  const allProfitCompletionTablesTotal = useMemo<number>(() => {
    // @ts-ignore
    return Number.parseInt(allProfitCompletionData?.total, 10);
  }, [allProfitCompletionData?.total]);

  const { isDownloading, setIsDownloading, handleDownload } = useDownload({
    fileName: getStudioModuleName(MODULE_NAME, studioName),
    csvDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_PROFIT_COMPLETION_CSV_SUBMIT',
    pdfDownloadLogKey:
      'STUDIO_OVERALL_DETAIL_FINANCE_PROFIT_COMPLETION_PDF_SUBMIT',
    node: document.querySelector(`.${PDF_DOWNLOAD_NODE_CLASSNAME}`),
    downloadService: createDownloadService(
      downloadProfitCompletionDataOfFinance,
    ),
  });

  const handleDownloadInComp = useMemoizedFn(async (payload) => {
    if (isDownloading) return;
    const params = {
      ...payload,
      studio_id: studioId,
      start_date: getFirstDayOfYear(filter?.date),
      end_date: getLastDayOfYear(filter?.date),
      file_type: Number.parseInt(payload?.fileType, 10),
    };
    if (params.fileType === FILE_TYPES_MAP.PDF) {
      setIsDownloading?.(true);
      await allProfitCompletionRun(params);
      setIsDownloading?.(false);
    }
    handleDownload?.(params);
  });

  // 筛选器变化，调接口刷新页面
  useEffect(() => {
    if (!studioId) return;
    if (!filter?.date) return;
    const params = {
      studio_id: studioId,
      start_date: getFirstDayOfYear(filter?.date),
      end_date: getLastDayOfYear(filter?.date),
    };
    profitCompletionRun(params);
  }, [studioId, filter?.date]);

  const tabs = useMemo(() => {
    if (profitCompletionLoading) return [];
    const isNoData =
      isBudgetActualOrForecastCompleteNoData(
        profitCompletionTrendsDataWithoutYTM,
      ) && isRevenueCompletionTableNoData(profitCompletionTablesData);
    return [
      {
        id: 'download',
        label: (
          <GeneralDownloadBtn
            uiType="2"
            fileTypes={FILE_TYPES1}
            isLoading={isDownloading}
            onConfirm={handleDownloadInComp}
          />
        ),
        component: isNoData ? (
          <NoData height={200} constomStyle={{ fontSize: 14 }} />
        ) : (
          <>
            <BarLineEchartCon data={profitCompletionTrendsDataWithoutYTM} />
            <MergeableTable
              module="profit"
              loading={profitCompletionLoading}
              date={filter?.date}
              total={profitCompletionTablesTotal}
              data={profitCompletionTablesData}
            />
          </>
        ),
      },
    ];
  }, [
    profitCompletionLoading,
    isDownloading,
    handleDownloadInComp,
    profitCompletionTrendsDataWithoutYTM,
    filter?.date,
    profitCompletionTablesTotal,
    profitCompletionTablesData,
  ]);

  const pdfTabs = useMemo(() => {
    if (profitCompletionLoading) return [];

    return [
      {
        id: 'table',
        label: null,
        component: (
          <MergeableTable
            module="profit"
            loading={allProfitCompletionLoading}
            date={filter?.date}
            total={allProfitCompletionTablesTotal}
            data={allRevenueCompletionTablesData}
          />
        ),
      },
    ];
  }, [
    allProfitCompletionLoading,
    filter?.date,
    allProfitCompletionTablesTotal,
    allRevenueCompletionTablesData,
  ]);

  return (
    <div
      style={style}
      className={classnames(
        'tea-card dbg-studio-overall-details-finance-profit-completion-card-container',
        className,
      )}
    >
      <TriggerViewCard
        className="dbg-studio-overall-details-finance-profit-completion-content"
        title={<H3>{MODULE_NAME}</H3>}
        loading={profitCompletionLoading}
        isNoData={false}
        tabs={tabs}
        defaultActiveId="download"
      />
      <TriggerViewCard
        className="dbg-studio-overall-details-finance-profit-completion-content"
        // 这个rootNodeClassName 是为了 下载PDF用的 不要删除
        rootNodeClassName={PDF_DOWNLOAD_NODE_CLASSNAME}
        rootNodeStyle={{ marginTop: 0 }}
        title={<H3>{MODULE_NAME}</H3>}
        loading={profitCompletionLoading}
        isNoData={false}
        tabs={pdfTabs}
        defaultActiveId="table"
      />
    </div>
  );
};

export default memo(ProfitCompletion);
